import { inject, Injectable } from '@angular/core';
import { CompanyProfile } from '@mkp/company/models';
import {
  selectCompanyProfiles,
  selectSelectedCompanyId,
} from './selectors/company-profile.selectors';
import { selectSelectedAccountId } from '@mkp/account/state';
import { CompanyResource } from '@mkp/company/data-access';
import { CompanyApiActions } from '@mkp/company/state/actions';
import { legalEntityDetailsActions } from '@mkp/legal-entity/actions';
import { FeatureDashboardActions } from '@mkp/user/feature-dashboard/actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, combineLatest, exhaustMap, filter, map, of, switchMap, take } from 'rxjs';

@Injectable()
export class CompanyEffects {
  private readonly actions$ = inject(Actions);
  private readonly companyResource = inject(CompanyResource);
  private readonly store = inject(Store);

  getCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeatureDashboardActions.enter),
      switchMap(() =>
        combineLatest([
          this.store
            .select(selectCompanyProfiles)
            .pipe(filter((companies) => companies.length > 0)),
          this.store.select(selectSelectedCompanyId).pipe(filter(Boolean)),
          this.store.select(selectSelectedAccountId).pipe(filter(Boolean)),
        ]).pipe(take(1))
      ),
      exhaustMap(([companies, selectedId, accountId]) =>
        this.companyResource
          .getCompanies({ params: getCompanyIdsFilter(companies), withAccountId: accountId })
          .pipe(
            map(({ companyProfiles }) =>
              CompanyApiActions.loadCompaniesSuccess({ companies: companyProfiles, selectedId })
            ),
            catchError((error) => of(CompanyApiActions.loadCompaniesFailure({ error })))
          )
      )
    )
  );

  loadCompaniesOnDetailsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(legalEntityDetailsActions.pageEntered),
      exhaustMap(({ legalEntityId }) =>
        this.companyResource.getCompanies({ filter: `legalEntity.id==${legalEntityId}` }).pipe(
          map(({ companyProfiles }) =>
            legalEntityDetailsActions.companiesLoadSuccess({ companies: companyProfiles })
          ),
          catchError((error) => of(legalEntityDetailsActions.companiesLoadFailure({ error })))
        )
      )
    )
  );
}

function getCompanyIdsFilter(companies: CompanyProfile[]) {
  return { filter: companies.map(({ id }) => `id==${id}`).join(',') };
}
